import { X, Tags } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

type Props = {
  allTypes: string[];
  selectedTypes: string[];
  setSelectedTypes: React.Dispatch<React.SetStateAction<string[]>>;
};

const TypeSelection = ({
  allTypes,
  selectedTypes,
  setSelectedTypes,
}: Props) => {
  const toggleTypeSelection = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  return (
    <div className="space-y-3">
      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {allTypes.length} نوع متاح
        </span>
        {selectedTypes.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedTypes([])}
            className="h-6 gap-1 px-2 text-xs text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
          >
            <X className="h-3 w-3" />
            مسح ({selectedTypes.length})
          </Button>
        )}
      </div>

      {/* Types Grid */}
      <div className="grid grid-cols-1 gap-2 w-full">
        {allTypes.length > 0 ?
          allTypes.map((type) => {
            const isSelected = selectedTypes.includes(type);
            return (
              <div
                key={type}
                className={cn(
                  "group flex items-center gap-3 rounded-md p-3 transition-colors cursor-pointer border w-full",
                  isSelected 
                    ? "bg-purple-50 border-purple-200 dark:bg-purple-950/30 dark:border-purple-800/50"
                    : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/50"
                )}
                onClick={() => toggleTypeSelection(type)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleTypeSelection(type)}
                  className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                />
                <div className="flex items-center gap-2 flex-1 min-w-0 max-w-full">
                  <Tags className={cn(
                    "h-4 w-4 flex-shrink-0",
                    isSelected ? "text-purple-600 dark:text-purple-400" : "text-slate-400"
                  )} />
                  <span className={cn(
                    "text-sm font-medium",
                    isSelected ? "font-medium text-purple-900 dark:text-purple-100" : "text-slate-700 dark:text-slate-300"
                  )}>
                    {type}
                  </span>
                </div>
                {isSelected && (
                  <Badge variant="secondary" className="h-5 px-2 text-xs">
                    محدد
                  </Badge>
                )}
              </div>
            );
          })
        : (
          <div className="flex flex-col items-center justify-center py-6 text-center w-full">
            <Tags className="h-8 w-8 text-slate-300 dark:text-slate-600 mb-2" />
            <p className="text-sm text-slate-500 dark:text-slate-500">
              لا توجد أنواع متاحة
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TypeSelection;
